import type { CompareMetric, CompareMetrics, CompareModuleKey } from '@/domain/locationCompare';

import type { MetricMenuGroup } from '../components/MetricMenu';

export interface MetricOption {
    id: string;
    module: CompareModuleKey;
    metricKey: string;
    /** i18n key, e.g. `metric.biomass.weight`. */
    labelKey: string;
    metric: CompareMetric;
    /**
     * Environmental params with no readings stay listed and cannot be selected.
     * That blocked list is intentional (upsell), including when the API omitted
     * the environment group entirely.
     */
    disabled?: boolean;
}

const EMPTY_METRIC: CompareMetric = {
    dataPoints: [],
    formatValue: () => '-',
};

const MODULE_ORDER: Array<{
    module: CompareModuleKey;
    metricKeys: string[];
}> = [
    { module: 'biomass', metricKeys: ['weight', 'conditionFactor', 'cv'] },
    { module: 'lice', metricKeys: ['female', 'moving', 'caligus'] },
    { module: 'wounds', metricKeys: ['any', 'active', 'healing', 'small', 'medium', 'large'] },
    // `mature_jaw` is intentionally not exposed: the chart treats `fully_mature`
    // as THE maturation metric (relabelled to "Maturation" in i18n).
    { module: 'maturation', metricKeys: ['fullyMature'] },
    { module: 'behavior', metricKeys: ['swimSpeed'] },
    {
        module: 'environment',
        metricKeys: [
            'oxygenConcentration',
            'oxygenSaturation',
            'temperature',
            'salinity',
            'averageDepth',
        ],
    },
];

function isCompareMetric(value: unknown): value is CompareMetric {
    return (
        typeof value === 'object' &&
        value !== null &&
        'dataPoints' in value &&
        Array.isArray(value.dataPoints) &&
        'formatValue' in value &&
        typeof value.formatValue === 'function'
    );
}

/** The metric a module group holds under `key`, which `MODULE_ORDER` names as a string. */
function metricIn(group: object, key: string): CompareMetric | undefined {
    const value: unknown = Reflect.get(group, key);
    return isCompareMetric(value) ? value : undefined;
}

/**
 * Flattens the grouped CompareMetrics into a stable ordered list of dropdown options.
 * Licensed modules the backend omitted are skipped. All five environmental
 * params are always listed — even when the API omitted the group — so they stay
 * visible as a blocked upsell. Params with no readings are disabled, not hidden.
 * Biomass/lice/etc. stay selectable even when every point is null.
 */
export interface BuildMetricOptions {
    /** Adds hourly depth and renames the daily series. Off, the dropdown is unchanged. */
    hourlyDepth?: boolean;
}

function environmentKeys(hourlyDepth: boolean): string[] {
    return [
        'oxygenConcentration',
        'oxygenSaturation',
        'temperature',
        'salinity',
        ...(hourlyDepth ? ['averageDepthHourly'] : []),
        'averageDepth',
    ];
}

export function buildMetricOptions(
    metrics: CompareMetrics,
    options: BuildMetricOptions = {}
): MetricOption[] {
    const hourlyDepth = options.hourlyDepth === true;
    const result: MetricOption[] = [];
    for (const entry of MODULE_ORDER) {
        const { module } = entry;
        const metricKeys =
            module === 'environment' ? environmentKeys(hourlyDepth) : entry.metricKeys;
        const group: object | undefined = metrics[module];
        if (!group && module !== 'environment') continue;
        for (const metricKey of metricKeys) {
            if (metricKey === 'averageDepthHourly') {
                result.push({
                    id: 'environment.averageDepthHourly',
                    module,
                    metricKey,
                    labelKey: 'metric.environment.averageDepthHourly',
                    // The series itself comes from the depth endpoint, not the compare payload.
                    // Daily depth can still be empty while hourly readings are already in,
                    // so the daily series must not block this option.
                    metric: EMPTY_METRIC,
                });
                continue;
            }
            const metric = group ? metricIn(group, metricKey) : undefined;
            if (!metric && module !== 'environment') continue;
            const resolved = metric ?? EMPTY_METRIC;
            result.push({
                id: `${module}.${metricKey}`,
                module,
                metricKey,
                labelKey:
                    metricKey === 'averageDepth' && hourlyDepth
                        ? 'metric.environment.averageDepthDaily'
                        : `metric.${module}.${metricKey}`,
                metric: resolved,
                disabled: module === 'environment' && !metricHasNumericData(resolved),
            });
        }
    }
    return result;
}

/** Prefer the caller's last choice when it is still enabled, otherwise the first enabled metric. */
export function pickSelectableMetric(
    metricOptions: MetricOption[],
    selectedMetricId: string | undefined
): MetricOption | undefined {
    return (
        metricOptions.find((option) => option.id === selectedMetricId && !option.disabled) ??
        metricOptions.find((option) => !option.disabled)
    );
}

function metricHasNumericData(metric: CompareMetric): boolean {
    return metric.dataPoints.some((point) =>
        Object.values(point.values).some((value) => value != null)
    );
}

/**
 * Builds the grouped structure consumed by the `<Dropdown>` molecule:
 * one group per CompareMetrics module, each containing its metric items.
 * Licensed modules with no options are skipped. The environmental group is
 * always present (disabled items included) because those params are listed
 * even without sensor data.
 *
 * Groups and items are sorted alphabetically by their translated label so the
 * dropdown is predictable across locales.
 */
export function buildMetricGroups(
    metricOptions: MetricOption[],
    t: (key: string) => string
): MetricMenuGroup[] {
    const collator = new Intl.Collator(undefined, { sensitivity: 'base' });

    const groupsByModule = new Map<CompareModuleKey, MetricOption[]>();
    for (const option of metricOptions) {
        const bucket = groupsByModule.get(option.module);
        if (bucket) bucket.push(option);
        else groupsByModule.set(option.module, [option]);
    }

    const groups = MODULE_ORDER.flatMap(({ module }) => {
        const items = groupsByModule.get(module);
        if (!items || items.length === 0) return [];
        const sortedOptions = items
            .map((opt) => ({
                value: opt.id,
                label: t(opt.labelKey),
                disabled: opt.disabled,
                title: opt.disabled ? t('metric.unavailable') : undefined,
            }))
            .sort((a, b) => collator.compare(a.label, b.label));
        return [{ label: t(`metric.group.${module}`), options: sortedOptions }];
    });

    return groups.sort((a, b) => collator.compare(a.label, b.label));
}

/**
 * True when there is nothing the user can chart — including the injected
 * environmental group when every param is disabled. An empty list counts as
 * disabled so the no-metrics empty state still shows.
 */
export function areAllMetricOptionsDisabled(groups: MetricMenuGroup[]): boolean {
    return groups.every((group) => group.options.every((option) => option.disabled));
}
